import { DOCUMENT, NgStyle } from '@angular/common';
import { Component, type ElementRef, effect, computed, inject, input, linkedSignal, output, signal, viewChild, untracked } from '@angular/core';

import { ResizeObserverDirective } from '@23degrees/directives/resize-observer.directive';
import { PanDirective } from '@23degrees/directives/pan.directive';
import { MeasureService } from '@23degrees/shared/services/measure/measure.service';

interface IFilterObject {
  label: string;
  value: string;
  date: string;
  selected: boolean;
}

interface IHammerEvent extends Event {
  type: string; // Name of the event. Like panstart.
  deltaX: number; // Movement of the X axis.
  deltaY: number; // Movement of the Y axis.
  deltaTime: number; // Total time in ms since the first input.
  distance: number; // Distance moved.
  angle: number; // Angle moved.
  velocityX: number; // Velocity on the X axis, in px/ms.
  velocityY: number; // Velocity on the Y axis, in px/ms
  velocity: number; // Highest velocityX/Y value.
  direction: string | number; // Direction moved. Matches the DIRECTION constants.
  offsetDirection: string; // Direction moved from it’s starting point. Matches the DIRECTION constants.
  scale: number; // Scaling that has been done when multi-touch. 1 on a single touch.
  rotation: number; // Rotation (in deg) that has been done when multi-touch. 0 on a single touch.
  center: { x: number; y: number }; // Center position for multi-touch, or just the single pointer.
  srcEvent: MouseEvent | TouchEvent | PointerEvent; // Source event object, type TouchEvent, MouseEvent or PointerEvent.
  target: EventTarget; // Target that received the event.
  pointerType: string; // Primary pointer type, could be touch, mouse, pen or kinect.
  eventType: string; // Event type, matches the INPUT constants.
  isFirst: boolean; // true when the first input.
  isFinal: boolean; // true when the final (last) input.
  pointers: unknown[]; // Array with all pointers, including the ended pointers (touchend, mouseup).
  changedPointers: unknown[]; // Array with all new/moved/lost pointers.
  preventDefault: Event['preventDefault']; // Reference to the srcEvent.preventDefault() method. Only for experts!
}

@Component({
  selector: 'items-slider',
  templateUrl: './items-slider.component.html',
  styleUrls: ['./items-slider.component.scss'],
  providers: [MeasureService],
  imports: [NgStyle, ResizeObserverDirective, PanDirective],
})
export class ItemsSliderComponent {
  private document = inject<Document>(DOCUMENT);
  private measureService = inject(MeasureService);

  readonly items = input.required<IFilterObject[]>();
  readonly selected = input.required<string>();
  readonly selectedChange = output<string>();

  readonly labelField = input('label');
  readonly valueField = input('value');

  readonly fill = input(false);
  readonly style = input<Partial<CSSStyleDeclaration>>({ color: '#b3b3b3' });

  readonly sliderRef = viewChild<ElementRef<HTMLDivElement>>('sliderRef');
  readonly movePointer = viewChild<ElementRef<HTMLDivElement>>('movePointer');

  public mainPointerLeft = signal(0);
  public movePointerLeft = signal(0);
  public fillWidth = signal(0);
  public labelWidth = signal(0);

  private state = linkedSignal(this.selected);

  private numSteps = computed(() => this.items().length - 1);
  private stateIdx = computed(() => this.items().findIndex((obj) => obj[this.valueField()] === this.state()));
  private selectedIdx = computed(() => this.items().findIndex((obj) => obj[this.valueField()] === this.selected()));

  constructor() {
    effect(this.updateLabel);
    effect(this.updatePointers);
  }

  public activeLabel = computed(() => {
    const items = this.items();
    if (items.length && this.state() !== undefined) {
      const item = items.find((obj) => obj[this.valueField()] === this.state());
      if (item) {
        return item[this.labelField()];
      }
    }

    return '';
  });

  private getIndexX = (idx: number) => {
    const bcRect = this.sliderRef().nativeElement.getBoundingClientRect();
    const ratio = bcRect.width / this.sliderRef().nativeElement.clientWidth;

    return Math.round(((idx / this.numSteps()) * bcRect.width) / ratio);
  };

  private getRelativeX = (clientX: number) => {
    const bcRect = this.sliderRef().nativeElement.getBoundingClientRect();
    return clientX <= bcRect.left ? 0 : clientX >= bcRect.right ? bcRect.width : clientX - bcRect.left;
  };

  private updateLabel = () => {
    const items = this.items();
    untracked(() => {
      let maxWidth = 0;

      for (const item of items) {
        const label = item[this.labelField()];
        const textWidth = this.measureService.calcTextWidth(label);
        maxWidth = Math.max(maxWidth, textWidth);
      }

      this.labelWidth.set(maxWidth);
    });
  };

  private updatePointers = () => {
    const idx = this.selectedIdx();
    if (idx !== -1) {
      untracked(() => {
        this.updateMainPointer(idx);
        this.updateMovePointer(idx);
      });
    }
  };

  private updateMainPointer = (idx: number) => {
    const xPos = this.getIndexX(idx);
    this.mainPointerLeft.set(xPos);
  };

  private updateMovePointer = (idx: number) => {
    const xPos = this.getIndexX(idx);
    this.movePointerLeft.set(xPos);
  };

  public handleMove = (event: Partial<IHammerEvent>, pushState = true) => {
    const bcRect = this.sliderRef().nativeElement.getBoundingClientRect();

    const xPos = this.getRelativeX(event.center.x);

    const relPos = xPos / bcRect.width;

    const idx = Math.round(this.numSteps() * relPos);
    const value = this.items()[idx][this.valueField()];

    this.updateMovePointer(idx);
    if (pushState) {
      this.updateMainPointer(idx);
    }

    this.state.set(value);
    if (pushState) {
      this.selectedChange.emit(value);
    }
  };

  public handleResize = (event: ResizeObserverEntry) => {
    this.updateLabel();
    this.updatePointers();
  };
}
